import {
  CrowdsecHealthResponse,
  InventoryPackage,
  RemoveServerResponse,
  ServerDetail,
  ServerGroup,
  ServerGroupUpdateResponse,
  ServerComplianceScore,
  ServerHardening,
  ServerInventory,
  ServerSummary,
  ServerVulnerabilities,
  ServerWebEnvironment,
  type CreateServerGroupRequest,
  type UpdateServerGroupRequest,
  type ServerListQuery,
  type UpdateServerRequest,
  type WebEnvironmentSettingInput,
} from '@bartizo/shared';
import { z } from 'zod';
import { apiPageRequest, apiRequest, type Page } from './client.js';

/**
 * Servers and groups (knowledge doc sections 15 and 7).
 *
 * The query is built here rather than by each caller, so a filter that does not
 * exist is a type error instead of a parameter the API quietly ignores.
 */

export type ServerFilters = Partial<Pick<ServerListQuery, 'status' | 'group_id' | 'q'>> & {
  limit?: number;
  cursor?: string;
};

const toQueryString = (filters: ServerFilters): string => {
  const params = new URLSearchParams();
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.group_id !== undefined) params.set('group_id', filters.group_id);
  if (filters.q !== undefined && filters.q.length > 0) params.set('q', filters.q);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.cursor !== undefined) params.set('cursor', filters.cursor);
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
};

export const listServers = (filters: ServerFilters = {}): Promise<Page<ServerSummary>> =>
  apiPageRequest(`/servers${toQueryString(filters)}`, { schema: ServerSummary });

export const getServer = (id: string): Promise<ServerDetail> =>
  apiRequest(`/servers/${encodeURIComponent(id)}`, { schema: ServerDetail });

/** P11.9.1: CrowdSec's own health on the server, as its agent last reported it. */
export const getCrowdsecHealth = (id: string): Promise<CrowdsecHealthResponse> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/crowdsec-health`, {
    schema: CrowdsecHealthResponse,
  });

/** P11.8.6: what the server's Wazuh agent found, as the central manager holds it. */
export const getServerHardening = (id: string): Promise<ServerHardening> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/hardening`, { schema: ServerHardening });

/** P11.8.7: what is on the server, from its Wazuh agent's inventory. */
export const getServerInventory = (id: string): Promise<ServerInventory> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/inventory`, { schema: ServerInventory });

/** Its packages by name, a page at a time; `q` a name or source package it contains. */
export const listServerPackages = (
  id: string,
  filters: { q?: string; cursor?: string } = {},
): Promise<Page<InventoryPackage>> => {
  const params = new URLSearchParams({ limit: '200' });
  if (filters.q !== undefined && filters.q !== '') params.set('q', filters.q);
  if (filters.cursor !== undefined) params.set('cursor', filters.cursor);
  return apiPageRequest(
    `/servers/${encodeURIComponent(id)}/inventory/packages?${params.toString()}`,
    {
      schema: InventoryPackage,
    },
  );
};

/** P11.8.8: the server's vulnerable packages, from central's advisory data. */
export const getServerVulnerabilities = (id: string): Promise<ServerVulnerabilities> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/vulnerabilities`, {
    schema: ServerVulnerabilities,
  });

/** P11.8.9: the server's score for each standard its assessment is tagged with. */
export const getServerCompliance = (id: string): Promise<ServerComplianceScore[]> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/compliance`, {
    schema: z.array(ServerComplianceScore),
  });

export const updateServer = (id: string, patch: UpdateServerRequest): Promise<ServerDetail> =>
  apiRequest(`/servers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
    schema: ServerDetail,
  });

export const approveServer = (id: string): Promise<ServerDetail> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    schema: ServerDetail,
  });

/** Section 12, High risk: the dialog in front of this asks for the password first. */
export const removeServer = (id: string): Promise<RemoveServerResponse> =>
  apiRequest(`/servers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: RemoveServerResponse,
  });

export const listServerGroups = (): Promise<ServerGroup[]> =>
  apiRequest('/server-groups', { schema: z.array(ServerGroup) });

export const createServerGroup = (body: CreateServerGroupRequest): Promise<ServerGroup> =>
  apiRequest('/server-groups', { method: 'POST', body, schema: ServerGroup });

/** A new name or new roles; new roles build each member a new settings version. */
export const updateServerGroup = (
  id: string,
  body: UpdateServerGroupRequest,
): Promise<ServerGroupUpdateResponse> =>
  apiRequest(`/server-groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    schema: ServerGroupUpdateResponse,
  });

/** Only an empty group that no token in use names; the API says which it is not. */
export const deleteServerGroup = (id: string): Promise<ServerGroup> =>
  apiRequest(`/server-groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: ServerGroup,
  });

/** Section 8.8: what the agent proposed, what an admin confirmed, and Cloudflare's list. */
export const getWebEnvironment = (id: string): Promise<ServerWebEnvironment> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/web-environment`, {
    schema: ServerWebEnvironment,
  });

/**
 * Section 12, High risk: which proxies may say who the visitor is. The dialog in
 * front of this asks for the password first.
 */
export const confirmWebEnvironment = (
  id: string,
  environment: WebEnvironmentSettingInput,
): Promise<ServerWebEnvironment> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/web-environment`, {
    method: 'PUT',
    body: environment,
    schema: ServerWebEnvironment,
  });

/** Also High risk: Bartizo's web configuration comes off the server again. */
export const withdrawWebEnvironment = (id: string): Promise<ServerWebEnvironment> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/web-environment`, {
    method: 'DELETE',
    schema: ServerWebEnvironment,
  });
